import React, { useCallback, useEffect, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import { LicenseInfo } from "@mui/x-license";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import MoneyLabApp from "./MoneyLabApp.jsx";
import {
  DRAFT_KEY,
  TOOL,
  defaultState,
  normalizeState,
  missionsFromState,
  totals,
} from "./state.js";
import "./money-lab.css";

const licenseKey = import.meta.env.VITE_MUI_X_LICENSE_KEY || "";
if (licenseKey) {
  LicenseInfo.setLicenseKey(licenseKey);
}

const theme = createTheme({
  palette: {
    primary: { main: "#2f8f5b" },
    secondary: { main: "#0e7c86" },
    background: { default: "#f3f7f8", paper: "#ffffff" },
    text: { primary: "#0b1c24", secondary: "#3d4f57" },
  },
  typography: {
    fontFamily: '"IBM Plex Sans", system-ui, sans-serif',
  },
  shape: { borderRadius: 10 },
});

async function guestApi(url, opts = {}) {
  if (String(url).includes(`/tools/${TOOL}`)) {
    if (String(opts.method || "GET").toUpperCase() === "PUT") {
      try {
        const body = JSON.parse(opts.body || "{}");
        return { payload: body.payload || {}, canWrite: true };
      } catch (_) {
        return { payload: {}, canWrite: true };
      }
    }
    return { payload: {}, canWrite: true };
  }
  return {};
}

function LabRoot({ opts }) {
  const api = typeof opts.api === "function" ? opts.api : guestApi;
  const learnerId = opts.learnerId || "local";
  const toast =
    typeof opts.toast === "function"
      ? opts.toast
      : (message) => {
          let node = document.querySelector(".app-toast");
          if (!node) {
            node = document.createElement("div");
            node.className = "app-toast";
            node.setAttribute("role", "status");
            document.body.appendChild(node);
          }
          node.textContent = message;
          node.classList.add("is-on");
          clearTimeout(toast._t);
          toast._t = setTimeout(() => node.classList.remove("is-on"), 2200);
        };
  const weekKey =
    opts.weekKey || new URLSearchParams(location.search).get("week") || "money_l1_prepare";
  const [state, setState] = useState(() => defaultState());
  const [canWrite, setCanWrite] = useState(opts.canWrite !== false);
  const [ready, setReady] = useState(false);
  const saveTimer = useRef(null);
  const onMissions = opts.onMissions;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const draft = localStorage.getItem(DRAFT_KEY(learnerId));
        let remote = { payload: {}, canWrite: true };
        try {
          remote = await api(`/api/progress/learners/${learnerId}/tools/${TOOL}`);
        } catch (_) {}
        if (cancelled) return;
        if (remote.payload && Object.keys(remote.payload).length) {
          setState(normalizeState(remote.payload));
        } else if (draft) {
          setState(normalizeState(JSON.parse(draft)));
        } else {
          setState(defaultState());
        }
        setCanWrite(opts.canWrite !== false && remote.canWrite !== false);
      } catch (_) {
        if (!cancelled) setState(defaultState());
      } finally {
        if (!cancelled) setReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [learnerId]);

  const persist = useCallback(() => {
    setState((current) => {
      const next = { ...current, labOpened: true };
      const tot = totals(next);
      next.budgetHealth = tot.budgetHealth;
      try {
        localStorage.setItem(DRAFT_KEY(learnerId), JSON.stringify(next));
      } catch (_) {}
      if (typeof onMissions === "function") onMissions(missionsFromState(next), next);
      clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(async () => {
        if (!canWrite) return;
        try {
          await api(`/api/progress/learners/${learnerId}/tools/${TOOL}`, {
            method: "PUT",
            body: JSON.stringify({ payload: next }),
          });
        } catch (_) {}
      }, 600);
      return next;
    });
  }, [api, canWrite, learnerId, onMissions]);

  // Emit missions whenever state changes (without double setState)
  useEffect(() => {
    if (!ready) return;
    try {
      localStorage.setItem(DRAFT_KEY(learnerId), JSON.stringify(state));
    } catch (_) {}
    if (typeof onMissions === "function") onMissions(missionsFromState(state), state);
  }, [state, ready, learnerId, onMissions]);

  if (!ready) {
    return (
      <div className="yc-lab money-lab app-panel">
        <p className="app-meta">Loading money lab…</p>
      </div>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <MoneyLabApp
        state={state}
        setState={setState}
        weekKey={weekKey}
        canWrite={canWrite}
        toast={toast}
      />
    </ThemeProvider>
  );
}

const roots = new WeakMap();

function render(mount, opts = {}) {
  if (!mount) return;
  let root = roots.get(mount);
  if (!root) {
    mount.replaceChildren();
    root = createRoot(mount);
    roots.set(mount, root);
  }
  root.render(<LabRoot opts={opts} />);
}

const api = {
  render,
  defaultState,
  missionsFromState,
  TOOL,
};

if (typeof window !== "undefined") {
  window.YCMoneyLab = api;
}

export default api;
